/**
 * Dispute Eligibility Utilities
 *
 * Determines dispute eligibility from evidence-based account severity.
 * Bureau differences alone are review/caution opportunities, not late negatives.
 * Positive/current accounts without evidence are not tagged REVIEW.
 */

import {
  getAccountSeverity,
  classifyAccountEvidence,
  isNegativeAccount,
} from './negativeAccountClassifier';
import { isLatePaymentStatus } from './paymentHistoryUtils';

/**
 * Check if the account has any strong dispute signals
 * @param {Object} account - The tradeline/account object
 * @returns {Object} - { hasSignal: boolean, signals: string[] }
 */
export const detectDisputeSignals = (account) => {
  if (!account) return { hasSignal: false, signals: [] };

  const evidence = classifyAccountEvidence(account);
  const signals = [];

  (evidence.negativeReasons || []).forEach((reason) => signals.push(reason));
  (evidence.bureauDifferenceReasons || []).forEach((reason) => signals.push(reason));

  return {
    hasSignal: signals.length > 0,
    signals,
    severity: evidence.severity,
  };
};

/**
 * Determine the dispute eligibility status for an account
 * @param {Object} account - The tradeline/account object
 * @returns {Object} - { status: 'DISPUTABLE'|'CAUTION'|'CURRENT', reason: string, signals: string[] }
 */
export const getDisputeEligibility = (account) => {
  if (!account) {
    return { status: 'CURRENT', reason: 'No account data', signals: [] };
  }

  const evidence = classifyAccountEvidence(account);
  const severity = evidence.severity;

  if (severity === 'negative') {
    return {
      status: 'DISPUTABLE',
      reason: evidence.negativeReasons?.[0] || 'Account has late/derogatory evidence',
      signals: evidence.negativeReasons || [],
    };
  }

  if (severity === 'bureau_difference') {
    return {
      status: 'CAUTION',
      reason: evidence.bureauDifferenceReasons?.[0] || 'Bureau values differ',
      signals: evidence.bureauDifferenceReasons || [],
    };
  }

  if (severity === 'current') {
    return {
      status: 'CAUTION',
      reason: 'This account appears current and positive with no clear dispute signals',
      signals: [],
    };
  }

  return {
    status: 'CURRENT',
    reason: 'Insufficient data to classify',
    signals: [],
  };
};

/**
 * Check if an account needs a caution warning before disputing
 * @param {Object} account - The tradeline/account object
 * @returns {boolean}
 */
export const needsCautionWarning = (account) => {
  const severity = getAccountSeverity(account);
  if (severity === 'negative') return false;
  if (severity === 'bureau_difference') return true;
  const eligibility = getDisputeEligibility(account);
  return eligibility.status === 'CAUTION';
};

/**
 * Calculate the number of unique late months from payment_history_grid
 * IMPORTANT: A single late month reported by multiple bureaus counts as 1, not 3
 *
 * @param {Object} account - The tradeline/account object
 * @returns {number} - Count of unique late months across all bureaus
 */
export const calculateUniqueLateMonths = (account) => {
  const paymentHistoryGrid = account?.payment_history_grid;
  if (!paymentHistoryGrid || typeof paymentHistoryGrid !== 'object') {
    return account?.late_payments || account?.latePaymentCount || 0;
  }

  const uniqueLateMonths = new Set();

  Object.values(paymentHistoryGrid).forEach((yearsData) => {
    if (typeof yearsData === 'object' && yearsData !== null) {
      Object.entries(yearsData).forEach(([year, monthsData]) => {
        if (typeof monthsData === 'object' && monthsData !== null) {
          Object.entries(monthsData).forEach(([month, status]) => {
            if (isLatePaymentStatus(status)) {
              uniqueLateMonths.add(`${year}-${String(month).padStart(2, '0')}`);
            }
          });
        }
      });
    }
  });

  return uniqueLateMonths.size;
};

/**
 * Check if account has charge-off status from payment_history_grid
 * @param {Object} account - The tradeline/account object
 * @returns {boolean}
 */
export const hasChargeOffInPaymentHistory = (account) => {
  const paymentHistoryGrid = account?.payment_history_grid;
  if (!paymentHistoryGrid || typeof paymentHistoryGrid !== 'object') {
    return false;
  }

  const CHARGEOFF_INDICATORS = ['CO', 'CHARGEOFF', 'CHARGE-OFF'];

  for (const yearsData of Object.values(paymentHistoryGrid)) {
    if (typeof yearsData === 'object' && yearsData !== null) {
      for (const monthsData of Object.values(yearsData)) {
        if (typeof monthsData === 'object' && monthsData !== null) {
          for (const status of Object.values(monthsData)) {
            const statusUpper = String(status || '').toUpperCase().trim();
            if (CHARGEOFF_INDICATORS.some((ind) => statusUpper.startsWith(ind))) {
              return true;
            }
          }
        }
      }
    }
  }

  return false;
};

export default {
  detectDisputeSignals,
  getDisputeEligibility,
  needsCautionWarning,
  calculateUniqueLateMonths,
  hasChargeOffInPaymentHistory,
  isNegativeAccount,
};
